import { computed, unref, type ComputedRef, type Ref } from 'vue';

export interface PriceUnitFormatted {
    label: string;
    unit_single: string;
    unit_plural: string;
}

export interface ProductData {
    // For FIXED deposits this is a Money-like object from backend
    // For METERED deposits this will be null
    deposit_amount?: unknown | null;
    // For METERED deposits this represents the quantity (e.g. hours, boxes)
    // For FIXED deposits this will be null
    deposit_quantity?: number | null;
    deposit_type?: string;
    // Final billed quantity, set once a VARIABLE service completes. Null until then.
    quantity?: number | null;
    price_unit?: string;
    price_unit_formatted?: PriceUnitFormatted;
}

export interface PriceUnitFormatting {
    depositQuantity: ComputedRef<string | number>;
    depositUnitLabel: ComputedRef<string>;
    finalQuantity: ComputedRef<string | number>;
    finalQuantityLabel: ComputedRef<string>;
}

/**
 * Both `deposit_quantity` and `quantity` are stored in the service's own `price_unit` — so a
 * HALF_HOUR service counts half-hours, because `price_amount` is the per-30-min rate. Customers
 * think in hours, so halve for display.
 */
function toDisplayQuantity(raw: number | null | undefined, priceUnit?: string): number | null {
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;

    return priceUnit === 'HALF_HOUR' ? raw / 2 : raw;
}

function toUnitLabel(displayed: number, priceUnit?: string, formatted?: PriceUnitFormatted): string {
    // The stored unit is half-hours, but the displayed figure is hours — so the label has to
    // come from the conversion, not from `price_unit_formatted` (which would read "30 mins").
    if (priceUnit === 'HALF_HOUR') {
        return displayed === 1 ? 'hour' : 'hours';
    }

    if (!formatted) return '';

    return displayed === 1 ? formatted.unit_single : formatted.unit_plural;
}

export function usePriceUnitFormatting(
    productData: Ref<ProductData> | ComputedRef<ProductData> | ProductData,
): PriceUnitFormatting {
    const data = computed(() => {
        return unref(productData);
    });

    const depositQuantity = computed(() => {
        const product = data.value;

        // Only apply quantity/unit formatting for METERED deposits
        if (product?.deposit_type === 'METERED') {
            return toDisplayQuantity(product.deposit_quantity, product.price_unit) ?? '-';
        }
        return '-';
    });

    const depositUnitLabel = computed(() => {
        const product = data.value;

        // Only provide unit labels for METERED deposits
        if (product?.deposit_type !== 'METERED' || !product || !product.price_unit_formatted) return '';

        // Use the displayed quantity for pluralization instead of raw deposit_quantity
        return toUnitLabel(Number(depositQuantity.value), product.price_unit, product.price_unit_formatted);
    });

    const finalQuantity = computed(() => {
        const product = data.value;

        return toDisplayQuantity(product?.quantity, product?.price_unit) ?? '-';
    });

    const finalQuantityLabel = computed(() => {
        const product = data.value;

        if (!product || typeof product.quantity !== 'number') return '';

        return toUnitLabel(Number(finalQuantity.value), product.price_unit, product.price_unit_formatted);
    });

    return {
        depositQuantity,
        depositUnitLabel,
        finalQuantity,
        finalQuantityLabel,
    };
}
